/*
 * File: StudentDrive.tsx
 * Author: Austin Robinson
 * Date Created: 7/11/2022
 * Copyright (c) DrivingDoc 2022
 */
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Grid from '@mui/material/GridLegacy';
import moment from 'moment';

import { CoursePeriod } from 'api/hooks/private/periods/GetCoursePeriod.ts';

interface PropTypes {
  driveNumber: string;
  isCurrent: boolean;
  registration: CoursePeriod['period_enrollments'][number]['student']['period_enrollments'][number];
}

export default function StudentDriveOverview(props: PropTypes) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { driveNumber, isCurrent, registration } = props;

  const getMoreInfo = () => {
    if (registration.attendance === 'absent' || registration.attendance === 'no_permit') {
      return (
        <Typography variant="subtitle1" sx={{ lineHeight: 'normal', color: 'red' }}>
          {registration.notes}
        </Typography>
      );
    }

    if (moment(registration.course_period.start).isBefore()) {
      return (
        <Button
          variant="contained"
          sx={{
            px: 1,
            py: 0,
          }}
          onClick={() => {
            // eslint-disable-next-line no-alert
            alert('Evaluations Coming Soon');
          }}
        >
          <Typography variant="button" sx={{ lineHeight: 'normal' }} noWrap>
            View Evaluation
          </Typography>
        </Button>
      );
    }
    return null;
  };

  const getHeader = () => {
    if (registration.course_period.deleted) {
      return (
        <Typography variant="h6" sx={{ lineHeight: 'normal', color: 'red', fontWeight: 'bold' }}>
          Deleted
        </Typography>
      );
    }
    if (registration.canceled_at) {
      const canceledOn = moment(registration.canceled_at);
      const deadline = moment(registration.course_period.start).subtract(24, 'hours');
      if (canceledOn.isAfter(deadline) && registration.canceled_by && !registration.late_cancel_override) {
        // need to verify late cancel override!!
        return (
          <Typography variant="h6" sx={{ lineHeight: 'normal', color: 'red', fontWeight: 'bold' }}>
            Canceled Late
          </Typography>
        );
      }
      return (
        <Typography variant="h6" sx={{ lineHeight: 'normal', color: 'orange', fontWeight: 'bold' }}>
          Canceled
        </Typography>
      );
    }
    if (registration.attendance === 'absent') {
      return (
        <Typography variant="h6" sx={{ lineHeight: 'normal', color: 'red', fontWeight: 'bold' }}>
          No Show
        </Typography>
      );
    }
    if (registration.attendance === 'no_permit') {
      return (
        <Typography variant="h6" sx={{ lineHeight: 'normal', color: 'red', fontWeight: 'bold' }}>
          No Permit
        </Typography>
      );
    }
    return (
      <Typography variant="h6" sx={{ lineHeight: 'normal' }}>
        Period #{driveNumber}
      </Typography>
    );
  };

  return (
    <Grid
      container
      direction="row"
      alignItems="stretch"
      sx={{
        border: isCurrent ? 5 : 1,
        borderColor: isCurrent ? 'RGB(52, 199, 89)' : '#000000',
        bgcolor: '#ffffff',
        // borderRadius: 1,
        boxShadow: 3,
        p: 0.5,
      }}
      gap={5}
      wrap="nowrap"
    >
      <Grid container direction="column">
        {getHeader()}
        <Grid container alignItems="center" wrap="nowrap" sx={{ ml: 1, mt: 1 }}>
          <AccountCircleIcon />
          <Typography variant="button" fontWeight="regular" color="text" textTransform="capitalize">
            {/* {`${registration.drive?.instructor?.firstName} ${registration.drive?.instructor?.lastName}`} */}
            Todo
          </Typography>
        </Grid>
        <Grid container alignItems="center" wrap="nowrap" sx={{ ml: 1 }}>
          <LocationOnIcon />
          <Typography variant="button" fontWeight="regular" color="text" textTransform="capitalize">
            Greenfield
          </Typography>
        </Grid>
      </Grid>

      <Grid container direction="column" justifyContent="space-between">
        <Grid container direction="column" justifyContent="flex-start" alignItems="flex-end" wrap="nowrap">
          <Typography variant="h6" sx={{ lineHeight: 'normal' }} noWrap>
            {moment(registration.course_period.start).format('dddd, MMMM Do')}
          </Typography>
          <Typography variant="h6" sx={{ lineHeight: 'normal' }} noWrap>
            {`${moment(registration.course_period.start).format('h:mm A')} - ${moment(registration.course_period.end).format('h:mm A')}`}
          </Typography>
        </Grid>
        <Grid container justifyContent="flex-end" alignItems="flex-end">
          {getMoreInfo()}
        </Grid>
      </Grid>
    </Grid>
  );
}
